import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { nanoid } from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'
import { apiResolveTicketAction } from 'services/ActionsService'
import { apiGenerateKBMockup, apiResolveChatAction } from 'services/ChatService'
import { ACTION_LABELS, PRIORITY_STYLES, formatCardText } from 'constants/actionTypes'
import { saveMockup, selectMockup, updateMockupDraft } from 'store/mockups/mockupsSlice'

const INLINE_ACTION_ARTIFACT_TYPES = new Set([
    'pending_actions_card',
    'kb_mockup_generation_card',
    'chat_suggested_actions_card',
])

const DETAIL_ARTIFACT_TYPES = new Set([
    'ticket_note_search_results_card',
    'tool_result_card',
    'source_list',
    'web_approval_request',
])

export const isChatDetailArtifact = (artifact) => {
    if (!artifact || INLINE_ACTION_ARTIFACT_TYPES.has(artifact.type)) {
        return false
    }

    return DETAIL_ARTIFACT_TYPES.has(artifact.type) || Boolean(artifact.generatedFromTool)
}

export const getChatDetailArtifacts = (messages = []) => {
    if (!Array.isArray(messages)) return []

    return messages.flatMap((message) => {
        const artifacts = Array.isArray(message?.artifacts) ? message.artifacts : []
        return artifacts
            .filter(isChatDetailArtifact)
            .map((artifact) => ({
                artifact,
                messageId: message.id || message.rowKey || '',
                messageTimestamp: message.timestamp || '',
                messageText: message.text || '',
            }))
    })
}

const formatJson = (value) => {
    if (typeof value === 'string') return value
    try {
        return JSON.stringify(value ?? null, null, 2)
    } catch (_error) {
        return String(value ?? '')
    }
}

const priorityTone = (priority = 'medium') => {
    const normalized = String(priority || 'medium').toLowerCase()
    if (normalized === 'critical') return PRIORITY_STYLES.high
    return PRIORITY_STYLES[normalized] || PRIORITY_STYLES.medium
}

const ArtifactShell = ({ title, children, isDark }) => (
    <div
        className={classNames(
            'mt-3 rounded-xl border p-3 text-sm',
            isDark ? 'border-gray-600 bg-gray-800/80 text-gray-100' : 'border-gray-200 bg-white text-gray-800'
        )}
    >
        {title && <div className="font-semibold mb-2">{title}</div>}
        {children}
    </div>
)

const safeJsonParse = (value, fallback = {}) => {
    if (!value || typeof value !== 'string') return fallback
    try {
        return JSON.parse(value)
    } catch (_error) {
        return fallback
    }
}

const PendingActionsArtifact = ({ artifact, isDark }) => {
    const [localStatus, setLocalStatus] = useState({})
    const [busy, setBusy] = useState({})
    const [error, setError] = useState('')
    const actions = useMemo(
        () => (Array.isArray(artifact.actions) ? artifact.actions.filter((action) => action?.status === 'pending') : []),
        [artifact.actions]
    )

    const resolveAction = async (action, status) => {
        const ticketId = artifact.ticketId || action.ticketId
        if (!ticketId || !action.id) return

        setError('')
        setBusy((state) => ({ ...state, [action.id]: status }))
        try {
            await apiResolveTicketAction({ ticketId, id: action.id, status })
            setLocalStatus((state) => ({ ...state, [action.id]: status }))
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Unable to resolve action.')
        } finally {
            setBusy((state) => ({ ...state, [action.id]: null }))
        }
    }

    return (
        <ArtifactShell title={artifact.title || 'Pending actions'} isDark={isDark}>
            <p className={classNames('text-xs mb-3', isDark ? 'text-gray-400' : 'text-gray-500')}>
                Suggestions only — nothing runs until you accept or dismiss an action.
            </p>
            {error && <div className="mb-3 rounded bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
            {actions.length === 0 ? (
                <p className={classNames('text-xs', isDark ? 'text-gray-400' : 'text-gray-500')}>No pending actions.</p>
            ) : (
                <div className="space-y-2">
                    {actions.map((action) => {
                        const tone = priorityTone(action.priority)
                        const resolved = localStatus[action.id]
                        const currentBusy = busy[action.id]
                        return (
                            <div
                                key={action.id}
                                className={classNames(
                                    'rounded-lg border-l-4 p-3',
                                    tone.border,
                                    isDark ? 'bg-gray-900/70' : 'bg-gray-50'
                                )}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-semibold">
                                            {ACTION_LABELS[action.type] || action.title || action.type}
                                        </div>
                                        <div className="text-xs opacity-80">{formatCardText(action)}</div>
                                        {action.detail && <div className="mt-1 text-xs opacity-80">{action.detail}</div>}
                                    </div>
                                    <span className={classNames('rounded-full px-2 py-0.5 text-[11px] font-semibold', tone.badge)}>
                                        {tone.label}
                                    </span>
                                </div>
                                {resolved ? (
                                    <div className="mt-2 text-xs font-semibold capitalize">{resolved}</div>
                                ) : (
                                    <div className="mt-3 flex gap-2">
                                        <button
                                            type="button"
                                            disabled={Boolean(currentBusy)}
                                            onClick={() => resolveAction(action, 'accepted')}
                                            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                                        >
                                            {currentBusy === 'accepted' ? 'Accepting…' : 'Accept'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={Boolean(currentBusy)}
                                            onClick={() => resolveAction(action, 'dismissed')}
                                            className={classNames(
                                                'rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-60',
                                                isDark ? 'bg-gray-700 text-gray-100' : 'bg-gray-200 text-gray-700'
                                            )}
                                        >
                                            {currentBusy === 'dismissed' ? 'Dismissing…' : 'Dismiss'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </ArtifactShell>
    )
}

const KBMockupGenerationArtifact = ({ artifact, isDark }) => {
    const dispatch = useDispatch()
    const mockups = useSelector((state) => state.mockups.items || {})
    const currentMockupId = useSelector((state) => state.mockups.currentMockupId)
    const mockupOrder = useSelector((state) => state.mockups.order || [])
    const [busyMode, setBusyMode] = useState('')
    const [status, setStatus] = useState('')
    const [error, setError] = useState('')

    const chatId = artifact.chatId
    const openMockupId = artifact.openMockupId || currentMockupId
    const openMockup = openMockupId ? mockups[openMockupId] : null
    const openTitle = openMockup?.title || artifact.openMockupTitle || 'Current KB Mockup'
    const canOverwrite = Boolean(openMockupId && openMockup)

    const applyGeneratedDraft = (mockupId, response, fallbackTitle) => {
        const generatedContent = response?.data?.data?.content || ''
        const generatedTitle = response?.data?.data?.name?.trim() || fallbackTitle || 'Untitled KB Mockup'

        if (generatedContent) {
            dispatch(
                updateMockupDraft({
                    id: mockupId,
                    draft: generatedContent,
                    title: generatedTitle,
                })
            )
        }
    }

    const generateIntoMockup = async ({ mode, mockupId, title, promptContext }) => {
        if (!chatId || !mockupId) return

        setBusyMode(mode)
        setStatus('')
        setError('')
        try {
            dispatch(selectMockup(mockupId))
            const response = await apiGenerateKBMockup({
                mockupId,
                chatId,
                sourceType: artifact.sourceType || 'chat_history',
                promptContext,
                titleHint: title || 'Chat Draft',
            })
            applyGeneratedDraft(mockupId, response, title)
            setStatus(mode === 'overwrite' ? `Updated ${title || 'the open mockup'}.` : `Created ${title || 'a new mockup'}.`)
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to generate KB mockup content.')
        } finally {
            setBusyMode('')
        }
    }

    const overwriteOpenMockup = () => {
        if (!canOverwrite) {
            setError('Open a KB mockup first, or choose Create new mockup.')
            return
        }

        generateIntoMockup({
            mode: 'overwrite',
            mockupId: openMockupId,
            title: openTitle,
            promptContext:
                (openMockup?.draftHtml && openMockup.draftHtml.trim()) ||
                'Update the open KB draft using the latest chat history.',
        })
    }

    const createNewMockup = async () => {
        if (!chatId) return

        const nextIndex = mockupOrder.length + 1
        const nextMockupId = `kb-${nanoid()}`
        const nextTitle = `KB Mockup ${nextIndex}`

        setBusyMode('new')
        setStatus('')
        setError('')
        try {
            await dispatch(
                saveMockup({
                    mockup: {
                        id: nextMockupId,
                        title: nextTitle,
                        sourceType: artifact.sourceType || 'chat_history',
                        sourceId: chatId,
                        metadata: { originSourceId: chatId, createdFrom: 'chat_action_card' },
                    },
                })
            ).unwrap()

            await generateIntoMockup({
                mode: 'new',
                mockupId: nextMockupId,
                title: nextTitle,
                promptContext: '',
            })
        } catch (err) {
            setBusyMode('')
            setError(err?.response?.data?.message || err?.message || 'Failed to create a new KB mockup.')
        }
    }

    return (
        <ArtifactShell title={artifact.title || 'Generate KB mockup from this chat'} isDark={isDark}>
            <p className={classNames('text-xs mb-3', isDark ? 'text-gray-400' : 'text-gray-500')}>
                No KB draft will be generated automatically. Choose whether to update the open draft or preserve it and create a new mockup.
            </p>
            {openMockupId && (
                <p className="mb-3 text-xs">
                    <strong>Open mockup:</strong> {openTitle}
                </p>
            )}
            {error && <div className="mb-3 rounded bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
            {status && <div className="mb-3 rounded bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{status}</div>}
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    disabled={Boolean(busyMode) || !canOverwrite}
                    onClick={overwriteOpenMockup}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                >
                    {busyMode === 'overwrite' ? 'Updating…' : 'Overwrite open mockup'}
                </button>
                <button
                    type="button"
                    disabled={Boolean(busyMode)}
                    onClick={createNewMockup}
                    className={classNames(
                        'rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-60',
                        isDark ? 'bg-gray-700 text-gray-100' : 'bg-gray-200 text-gray-700'
                    )}
                >
                    {busyMode === 'new' ? 'Creating…' : 'Create new mockup'}
                </button>
            </div>
        </ArtifactShell>
    )
}

const ChatSuggestedActionsArtifact = ({ artifact, isDark }) => {
    const dispatch = useDispatch()
    const mockupOrder = useSelector((state) => state.mockups.order || [])
    const [localStatus, setLocalStatus] = useState({})
    const [busy, setBusy] = useState({})
    const [error, setError] = useState('')
    const [statusMessage, setStatusMessage] = useState('')
    const actions = useMemo(
        () => (Array.isArray(artifact.actions) ? artifact.actions.filter((action) => action?.status !== 'dismissed') : []),
        [artifact.actions]
    )

    const createMockupFromAction = async (action) => {
        const chatId = artifact.chatId || action.chatId
        if (!chatId) return

        const payload = safeJsonParse(action?.payload?.value, {})
        const nextIndex = mockupOrder.length + 1
        const nextMockupId = `kb-${nanoid()}`
        const nextTitle = payload.title || payload.kbTitle || action.title || `KB Mockup ${nextIndex}`

        await dispatch(
            saveMockup({
                mockup: {
                    id: nextMockupId,
                    title: nextTitle,
                    sourceType: 'chat_history',
                    sourceId: chatId,
                    metadata: { originSourceId: chatId, createdFrom: 'chat_suggested_action', actionId: action.id },
                },
            })
        ).unwrap()

        dispatch(selectMockup(nextMockupId))
        const response = await apiGenerateKBMockup({
            mockupId: nextMockupId,
            chatId,
            sourceType: 'chat_history',
            promptContext: payload.promptContext || action.detail || '',
            titleHint: nextTitle,
        })
        const generatedContent = response?.data?.data?.content || ''
        const generatedTitle = response?.data?.data?.name?.trim() || nextTitle
        if (generatedContent) {
            dispatch(updateMockupDraft({ id: nextMockupId, draft: generatedContent, title: generatedTitle }))
        }

        return {
            mockupId: nextMockupId,
            mockupTitle: generatedTitle,
        }
    }

    const resolveAction = async (action, status) => {
        if (!action?.id) return

        setError('')
        setStatusMessage('')
        setBusy((state) => ({ ...state, [action.id]: status }))
        try {
            let resolvedState = { status }
            if (status === 'accepted' && action.type === 'create_kb_mockup') {
                const mockupResult = await createMockupFromAction(action)
                if (mockupResult?.mockupId) {
                    await apiResolveChatAction({
                        chatId: artifact.chatId,
                        action,
                        status,
                        ticketId: action.ticketId,
                        resolvePayload: {
                            mockupId: mockupResult.mockupId,
                            mockupTitle: mockupResult.mockupTitle || '',
                        },
                    })
                    resolvedState = {
                        status,
                        ...mockupResult,
                    }
                }
            } else {
                const response = await apiResolveChatAction({
                    chatId: artifact.chatId,
                    action,
                    status,
                    ticketId: action.ticketId,
                    resolvePayload: action.resolvePayload || null,
                })
                const executorMessage = response?.data?.data?.executorResult?.message
                if (executorMessage) {
                    setStatusMessage(executorMessage)
                }
            }
            setLocalStatus((state) => ({ ...state, [action.id]: resolvedState }))
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Unable to resolve chat action.')
        } finally {
            setBusy((state) => ({ ...state, [action.id]: null }))
        }
    }

    if (actions.length === 0) return null

    return (
        <ArtifactShell title={artifact.title || 'Suggested actions'} isDark={isDark}>
            <p className={classNames('text-xs mb-3', isDark ? 'text-gray-400' : 'text-gray-500')}>
                These are saved with this chat only. Accepting a ticket-context action records the result to the ticket summary/timeline, but it is never added to everyone&apos;s pending actions queue.
            </p>
            {error && <div className="mb-3 rounded bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
            {statusMessage && <div className="mb-3 rounded bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{statusMessage}</div>}
            <div className="space-y-2">
                {actions.map((action) => {
                    const tone = priorityTone(action.priority)
                    const resolved =
                        localStatus[action.id] ||
                        (action.status && action.status !== 'pending'
                            ? {
                                status: action.status,
                                mockupId: action.mockupId || '',
                                mockupTitle: action.mockupTitle || '',
                            }
                            : null)
                    const currentBusy = busy[action.id]
                    const isExecutable = action.type === 'create_kb_mockup' || Boolean(action.ticketId)
                    return (
                        <div
                            key={action.id}
                            className={classNames(
                                'rounded-lg border-l-4 p-3',
                                tone.border,
                                isDark ? 'bg-gray-900/70' : 'bg-gray-50'
                            )}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-semibold">
                                        {action.type === 'create_kb_mockup' ? 'Create KB mockup' : ACTION_LABELS[action.type] || action.title || action.type}
                                    </div>
                                    <div className="text-xs opacity-80">{action.title}</div>
                                    {action.detail && <div className="mt-1 text-xs opacity-80">{action.detail}</div>}
                                    {action.ticketId && <div className="mt-1 text-[11px] opacity-70">Ticket #{action.ticketId}</div>}
                                </div>
                                <span className={classNames('rounded-full px-2 py-0.5 text-[11px] font-semibold', tone.badge)}>
                                    {tone.label}
                                </span>
                            </div>
                            {resolved ? (
                                resolved.status === 'accepted' && resolved.mockupId ? (
                                    <div className="mt-2 text-xs font-semibold">
                                        <a
                                            href={`/mockups/${encodeURIComponent(resolved.mockupId)}`}
                                            onClick={() => dispatch(selectMockup(resolved.mockupId))}
                                            className="text-blue-600 underline underline-offset-2 dark:text-blue-300"
                                        >
                                            Open KB draft{resolved.mockupTitle ? `: ${resolved.mockupTitle}` : ''}
                                        </a>
                                    </div>
                                ) : (
                                    <div className="mt-2 text-xs font-semibold capitalize">{resolved.status}</div>
                                )
                            ) : (
                                <div className="mt-3 flex gap-2">
                                    <button
                                        type="button"
                                        disabled={Boolean(currentBusy) || !isExecutable}
                                        onClick={() => resolveAction(action, 'accepted')}
                                        className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                                    >
                                        {currentBusy === 'accepted' ? 'Accepting…' : 'Accept'}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={Boolean(currentBusy)}
                                        onClick={() => resolveAction(action, 'dismissed')}
                                        className={classNames(
                                            'rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-60',
                                            isDark ? 'bg-gray-700 text-gray-100' : 'bg-gray-200 text-gray-700'
                                        )}
                                    >
                                        {currentBusy === 'dismissed' ? 'Dismissing…' : 'Dismiss'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </ArtifactShell>
    )
}

const TicketNoteSearchResultsArtifact = ({ artifact, isDark }) => {
    const matches = useMemo(() => (Array.isArray(artifact.matches) ? artifact.matches : []), [artifact.matches])
    const metadata = artifact.metadata || {}
    const scope = artifact.scope || {}
    const isCandidateSearch = artifact.generatedFromTool === 'search_ticket_notes_for_candidates' || matches.some((match) => Array.isArray(match.evidence))
    const ticketUrl = artifact.ticketId ? `/tickets/${artifact.ticketId}` : ''

    const renderSnippet = (match, index, parentTicket = {}) => {
        const ticketId = match.ticketId || parentTicket.ticketId || artifact.ticketId
        return (
            <div
                key={`${ticketId || 'ticket'}-${match.id || match.noteId || 'note'}-${index}`}
                className={classNames(
                    'rounded-lg border p-3',
                    isDark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-gray-50'
                )}
            >
                <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
                    {ticketId && !artifact.ticketId && (
                        <a href={`/tickets/${ticketId}`} className="font-semibold text-blue-500 underline">
                            Ticket #{ticketId}
                        </a>
                    )}
                    <span className={classNames('rounded-full px-2 py-0.5 font-semibold', isDark ? 'bg-blue-900/60 text-blue-100' : 'bg-blue-100 text-blue-700')}>
                        Score {match.score ?? '—'}
                    </span>
                    <span className={classNames('rounded-full px-2 py-0.5 font-semibold', isDark ? 'bg-gray-800 text-gray-200' : 'bg-gray-200 text-gray-700')}>
                        {match.visibility || match.noteType || 'Note'}
                    </span>
                    {match.timestamp && <span className="opacity-70">{match.timestamp}</span>}
                    {match.author && <span className="opacity-70">by {match.author}</span>}
                </div>
                <p className="whitespace-pre-wrap text-xs leading-relaxed">{match.snippet || ''}</p>
                {Array.isArray(match.matchedTerms) && match.matchedTerms.length > 0 && (
                    <div className={classNames('mt-2 text-[11px]', isDark ? 'text-gray-400' : 'text-gray-500')}>
                        Matched: {match.matchedTerms.join(', ')}
                    </div>
                )}
                {match.sensitiveRedactions > 0 && (
                    <div className="mt-2 text-[11px] font-semibold text-amber-600">
                        Sensitive content was redacted from this snippet.
                    </div>
                )}
            </div>
        )
    }

    return (
        <ArtifactShell title={artifact.title || 'Ticket note search'} isDark={isDark}>
            <div className={classNames('mb-3 space-y-1 text-xs', isDark ? 'text-gray-300' : 'text-gray-600')}>
                {artifact.query && (
                    <div>
                        <strong>Query:</strong> {artifact.query}
                    </div>
                )}
                <div>
                    <strong>Scope:</strong> {artifact.noteSource || 'allNotes'}
                    {Array.isArray(artifact.noteTypes) && artifact.noteTypes.length > 0 ? ` · ${artifact.noteTypes.join(', ')}` : ''}
                    {metadata.hasMore ? ' · more notes may exist' : ''}
                    {metadata.hasMoreCandidates ? ' · more candidate tickets may exist' : ''}
                </div>
                {isCandidateSearch && (
                    <>
                        {(scope.companyName || scope.companyId || scope.requesterName || scope.contactId) && (
                            <div>
                                <strong>Candidate bounds:</strong>{' '}
                                {[scope.companyName || (scope.companyId ? `Company #${scope.companyId}` : ''), scope.requesterName || (scope.contactId ? `Contact #${scope.contactId}` : '')]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </div>
                        )}
                        {(scope.dateStart || scope.dateEnd) && (
                            <div>
                                <strong>Date window:</strong> {scope.dateStart || 'Any start'} → {scope.dateEnd || 'Now'}
                                {metadata.defaultLookbackApplied ? ' · default recent lookback' : ''}
                            </div>
                        )}
                        <div>
                            <strong>Candidates searched:</strong> {metadata.searchedTicketCount ?? metadata.candidateReturnedCount ?? artifact.candidateTickets?.length ?? 0}
                            {artifact.totalMatchingTickets !== undefined ? ` · matching tickets: ${artifact.totalMatchingTickets}` : ''}
                            {scope.boardDefaultApplied ? ' · board #1 default' : ''}
                        </div>
                    </>
                )}
                {ticketUrl && (
                    <a href={ticketUrl} className="text-blue-500 underline">
                        Open ticket workspace
                    </a>
                )}
            </div>

            {artifact.warning && matches.length === 0 && (
                <div className={classNames('rounded px-3 py-2 text-xs', isDark ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-600')}>
                    {artifact.warning}
                </div>
            )}

            {matches.length > 0 && (
                <div className="space-y-2">
                    {matches.map((match, index) => {
                        if (!Array.isArray(match.evidence)) {
                            return renderSnippet(match, index)
                        }

                        return (
                            <div
                                key={`${match.ticketId || 'ticket'}-${index}`}
                                className={classNames(
                                    'rounded-lg border p-3',
                                    isDark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-gray-50'
                                )}
                            >
                                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <a href={`/tickets/${match.ticketId}`} className="font-semibold text-blue-500 underline">
                                            Ticket #{match.ticketId}
                                        </a>
                                        {match.summary && <div className="mt-1 text-xs font-semibold">{match.summary}</div>}
                                        <div className={classNames('mt-1 text-[11px]', isDark ? 'text-gray-400' : 'text-gray-500')}>
                                            {[match.companyName, match.contactName, match.statusName, match.lastUpdated ? `Updated ${match.lastUpdated}` : '']
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </div>
                                    </div>
                                    <span className={classNames('rounded-full px-2 py-0.5 text-[11px] font-semibold', isDark ? 'bg-blue-900/60 text-blue-100' : 'bg-blue-100 text-blue-700')}>
                                        Score {match.score ?? '—'}
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {match.evidence.map((evidence, evidenceIndex) => renderSnippet(evidence, evidenceIndex, match))}
                                </div>
                                {match.sensitiveRedactions > 0 && (
                                    <div className="mt-2 text-[11px] font-semibold text-amber-600">
                                        Sensitive content was redacted from one or more snippets.
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </ArtifactShell>
    )
}

const SimpleArtifact = ({ artifact, isDark }) => {
    if (artifact.type === 'tool_result_card') {
        const statusTone = artifact.status === 'error'
            ? 'bg-red-100 text-red-700 border-red-200'
            : artifact.status === 'warn'
                ? 'bg-amber-100 text-amber-700 border-amber-200'
                : 'bg-emerald-100 text-emerald-700 border-emerald-200'

        return (
            <ArtifactShell title={artifact.title || artifact.toolName || 'Tool result'} isDark={isDark}>
                <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className={classNames('rounded-full border px-2 py-0.5 font-semibold', statusTone)}>
                        {artifact.status || 'success'}
                    </span>
                    {artifact.toolName && <span className="font-mono opacity-80">{artifact.toolName}</span>}
                    {artifact.api && <span className="opacity-60">{artifact.api}</span>}
                </div>
                <div className="space-y-3">
                    <div>
                        <div className={classNames('mb-1 text-[11px] font-semibold uppercase tracking-wide', isDark ? 'text-gray-400' : 'text-gray-500')}>
                            Arguments
                        </div>
                        <pre className={classNames('max-h-48 overflow-auto rounded-lg p-3 text-xs', isDark ? 'bg-gray-950 text-gray-100' : 'bg-gray-100 text-gray-800')}>
                            {formatJson(artifact.args || {})}
                        </pre>
                    </div>
                    <div>
                        <div className={classNames('mb-1 text-[11px] font-semibold uppercase tracking-wide', isDark ? 'text-gray-400' : 'text-gray-500')}>
                            Result
                        </div>
                        <pre className={classNames('max-h-96 overflow-auto rounded-lg p-3 text-xs', isDark ? 'bg-gray-950 text-gray-100' : 'bg-gray-100 text-gray-800')}>
                            {formatJson(artifact.result || artifact.warning || artifact.error || {})}
                        </pre>
                    </div>
                </div>
            </ArtifactShell>
        )
    }

    if (artifact.type === 'code_block') {
        return (
            <ArtifactShell title={artifact.title || 'Code'} isDark={isDark}>
                <pre className={classNames('overflow-x-auto rounded-lg p-3 text-xs', isDark ? 'bg-gray-950' : 'bg-gray-100')}>
                    <code>{artifact.code || artifact.text || ''}</code>
                </pre>
            </ArtifactShell>
        )
    }

    if (artifact.type === 'source_list') {
        const sources = Array.isArray(artifact.sources) ? artifact.sources : []
        return (
            <ArtifactShell title={artifact.title || 'Sources'} isDark={isDark}>
                <ul className="list-disc space-y-1 pl-5 text-xs">
                    {sources.map((source, index) => (
                        <li key={`${source.url || source.title || 'source'}-${index}`}>
                            {source.url ? (
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                                    {source.title || source.url}
                                </a>
                            ) : (
                                source.title || source.id || 'Source'
                            )}
                        </li>
                    ))}
                </ul>
            </ArtifactShell>
        )
    }

    if (artifact.type === 'web_approval_request') {
        return (
            <ArtifactShell title={artifact.title || 'Web lookup approval'} isDark={isDark}>
                <p className="text-xs">{artifact.reason || artifact.text || 'This lookup requires approval before web search is used.'}</p>
                {artifact.query && <p className="mt-2 text-xs"><strong>Query:</strong> {artifact.query}</p>}
            </ArtifactShell>
        )
    }

    if (artifact.type === 'ticket_card' || artifact.type === 'kb_article_preview') {
        return (
            <ArtifactShell title={artifact.title || artifact.name || artifact.type} isDark={isDark}>
                <p className="text-xs whitespace-pre-wrap">{artifact.summary || artifact.text || artifact.description || ''}</p>
            </ArtifactShell>
        )
    }

    return null
}

const ChatArtifacts = ({ artifacts = [], isDark = false, mode = 'inline' }) => {
    const safeArtifacts = Array.isArray(artifacts) ? artifacts : []
    const renderableArtifacts = safeArtifacts.filter((artifact) => {
        if (mode === 'all') return true
        const isDetail = isChatDetailArtifact(artifact)
        return mode === 'details' ? isDetail : !isDetail
    })

    if (renderableArtifacts.length === 0) return null

    return (
        <div className="mt-2 space-y-2">
            {renderableArtifacts.map((artifact, index) => {
                if (artifact?.type === 'pending_actions_card') {
                    return <PendingActionsArtifact key={`artifact-${index}-${artifact.type}`} artifact={artifact} isDark={isDark} />
                }
                if (artifact?.type === 'kb_mockup_generation_card') {
                    return <KBMockupGenerationArtifact key={`artifact-${index}-${artifact.type}`} artifact={artifact} isDark={isDark} />
                }
                if (artifact?.type === 'chat_suggested_actions_card') {
                    return <ChatSuggestedActionsArtifact key={`artifact-${index}-${artifact.type}`} artifact={artifact} isDark={isDark} />
                }
                if (artifact?.type === 'ticket_note_search_results_card') {
                    return <TicketNoteSearchResultsArtifact key={`artifact-${index}-${artifact.type}`} artifact={artifact} isDark={isDark} />
                }
                return <SimpleArtifact key={`artifact-${index}-${artifact?.type || 'unknown'}`} artifact={artifact} isDark={isDark} />
            })}
        </div>
    )
}

export default React.memo(ChatArtifacts)
